/**
 * ¿Estamos en el subdominio de un asesor? (`lorena-gauna.inmobiliariaimperium.com`)
 *
 * La SPA de Vite no tiene middleware —eso es de Next—, así que el ruteo por host se
 * decide en el cliente: el `vercel.json` sirve el mismo `index.html` para cualquier
 * host, y acá se mira dónde estamos parados.
 *
 * Se resuelve sobre una lista de dominios propios y no sobre "¿tiene un punto?":
 * `www.`, `ficha.` y los `*.vercel.app` de preview también son subdominios y no son
 * portfolios de nadie.
 */

/** Dominios donde un subdominio SÍ significa "portfolio de un asesor". */
const DOMINIOS_CON_PORTFOLIO = [
  'inmobiliariaimperium.com',
  'imperium.uy',
  /**
   * Para probar el ruteo por subdominio en local sin tocar /etc/hosts: `lvh.me` y todos
   * sus subdominios resuelven a 127.0.0.1, así que `gustavo-qa.lvh.me:4000` entra por
   * el mismo camino que el subdominio de verdad. Sin esto, lo único probable en local
   * es `/asesor/<slug>`, que es otro camino — y el que se rompe es siempre el otro.
   */
  'lvh.me',
  /**
   * Chrome, Safari y Firefox resuelven cualquier `*.localhost` a 127.0.0.1 sin tocar
   * /etc/hosts, así que `gustavo-qa.localhost:4000` es la forma más corta de probar el
   * ruteo por subdominio sin salir de la máquina.
   */
  'localhost',
];

/**
 * Subdominios del producto: nunca son un asesor. Es la misma idea que
 * `SLUGS_RESERVADOS` del backend — acá alcanza con los que existen de verdad, porque
 * el backend ya rechaza que alguien tome uno reservado.
 */
const NO_SON_ASESORES = new Set(['www', 'app', 'api', 'ficha', 'portal', 'admin', 'blog', 'staging', 'dev', 'eventos']);

export function slugDelSubdominio(host = typeof window !== 'undefined' ? window.location.hostname : ''): string | null {
  const limpio = String(host ?? '').toLowerCase().split(':')[0];
  if (!limpio) return null;

  const dominio = DOMINIOS_CON_PORTFOLIO.find((d) => limpio.endsWith(`.${d}`));
  if (!dominio) return null;

  const sub = limpio.slice(0, -(dominio.length + 1));
  // Solo el primer nivel: `algo.lorena.inmobiliariaimperium.com` no es un asesor, y
  // además el certificado wildcard tampoco lo cubre.
  if (!sub || sub.includes('.')) return null;
  if (NO_SON_ASESORES.has(sub)) return null;

  return sub;
}

/** ¿Esta visita entró por el subdominio de un asesor? */
export function esHostDeAsesor(host?: string): boolean {
  return slugDelSubdominio(host) !== null;
}
